import { Grid, Stack, Tooltip, Typography, Box } from "@mui/material";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button, ButtonVariantOption } from "@/components/Button";
import { CustomModal } from "@/components/Modal";
import { CTextField } from "@/components/TextField/CTextField";
import { PageTitle } from "@/components/Title";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { getOwnerAnalyzeFileApi } from "@/features/ownerInfo/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { useApiClient } from "@/hooks/useApiClient";
import { Label } from "@/components/Label";
import HelpIcon from "@mui/icons-material/Help";
import { HttpStatusCode, isAxiosError } from "axios";
import { READ_ONLY_TOOLTIP_MESSAGE } from "@/utils/permissions";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  requestId: number;
  fallBackName: string;
}

export const DmMaxBranchNumberModal: React.FC<IProps> = (props: IProps) => {
  const DEFAULT_MAX_BRANCH_NUMBER = 10;

  const { open, setOpen, requestId, fallBackName } = props;
  const { apiClient } = useApiClient();

  const maxBrachNumberSchema = z.object({
    maxBranchNumber: z
      .string()
      .regex(/^[0-9]+$/, { message: "0以上の整数を設定してください" }),
  });
  type maxBrachNumberForm = z.infer<typeof maxBrachNumberSchema>;

  const reactHookForm = useForm<maxBrachNumberForm>({
    mode: "all",
    resolver: zodResolver(maxBrachNumberSchema),
    defaultValues: {
      maxBranchNumber: String(DEFAULT_MAX_BRANCH_NUMBER),
    },
  });

  const handleSubmitButton = (): void => {
    if (Number.isNaN(requestId)) {
      toast.error("ファイルのダウンロードに失敗しました");
      reactHookForm.reset();
      setOpen(false);
      return;
    }

    void getOwnerAnalyzeFileApi(apiClient, {
      requestId,
      fallBackName,
      resType: "excel_direct_mail",
      maxBranchNumber: Number(reactHookForm.getValues("maxBranchNumber")),
    })
      .then(() => {
        reactHookForm.reset();
        setOpen(false);
      })
      .catch((error) => {
        if (
          isAxiosError(error) &&
          error.response?.status === HttpStatusCode.Forbidden
        ) {
          toast.error(READ_ONLY_TOOLTIP_MESSAGE);
          return;
        }
        toast.error("ファイルのダウンロードに失敗しました");
      });
  };

  return (
    <>
      <CustomModal
        isOpen={open}
        handleClose={(
          event: Record<string, unknown>,
          reason: "backdropClick" | "escapeKeyDown"
        ): void => {
          if (
            reason &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            return;
          }
          setOpen(false);
        }}
        ariaLabelledby="dm-max-branch-number-modal"
        ariaDescribedby="dm-max-branch-number-modal-description"
        maxHeight={"90vh"}
        overflow="scroll"
      >
        <Stack direction={"column"} spacing={3}>
          {/* タイトル */}
          <PageTitle>共有人数の入力</PageTitle>
          <Typography>共有人数が何人以上の不動産を除外しますか？</Typography>
          {/* テキスト */}
          <Grid container spacing={2} justifyContent={"center"}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={3} sx={{ mt: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Label required={true}>共有人数</Label>
                    <Tooltip
                      title="10人以上→共有人数9人以下の不動産のみ抽出"
                      placement="bottom"
                    >
                      <HelpIcon sx={{ ml: 0.5, fontSize: "1.2rem" }} />
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={9}>
                  <CTextField
                    id={"max-branch-number-input"}
                    label={"人数"}
                    name={"maxBranchNumber"}
                    control={reactHookForm.control}
                    size={SizingWrapperStyle.LARGE}
                    inputProps={{
                      type: "number",
                      placeholder: "0以上の整数を入力してください",
                    }}
                    type={"range"}
                    autoFocus={true}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {/* ボタン */}
          <Stack
            direction={"row"}
            spacing={2}
            marginTop={2}
            justifyContent={"right"}
          >
            <Button
              label="キャンセル"
              onClick={() => {
                reactHookForm.reset();
                setOpen(false);
              }}
              size={SizingWrapperStyle.SMALL}
              variant={ButtonVariantOption.Outlined}
            />
            <Button
              label="DM対象者リスト（案）ダウンロード"
              onClick={handleSubmitButton}
              disabled={!reactHookForm.formState.isValid}
              size={SizingWrapperStyle.LARGE}
              variant={ButtonVariantOption.Contained}
            />
          </Stack>
        </Stack>
      </CustomModal>
    </>
  );
};
